import { FC, useEffect, useState } from 'react';
import { Button, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Content, PageSectionHeader } from '@/components';
import {
  CalculatorSettings,
  KPISummary,
  RatesTable,
  AddRateModal,
  EmptyState,
  SavePlanModal
} from './components';
import { useEU6Calculator } from './hooks/useEU6Calculator';
import { isTotalDeliveryPercentageOver100 } from './sumRateCardsDeliveryPercentage';
import { PageContainer, TableCard, SectionHeading } from './EU6Calculator.styles';
import { useCreateEU6CalculatorPlanMutation } from '@/api';
import { NAMESPACE } from '@/constants';
import type { RateCard } from './EU6Calculator.types';

const EU6Calculator: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const {
    settings,
    rateCards,
    kpiMetrics,
    isModalOpen,
    fxRates,
    updateSettings,
    updateFXRates,
    addRateCard,
    duplicateRateCard,
    updateRateCard,
    deleteRateCard,
    openModal,
    closeModal
  } = useEU6Calculator();

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [editingRateCard, setEditingRateCard] = useState<RateCard | null>(null);
  const [createPlan, { isLoading: isSaving }] = useCreateEU6CalculatorPlanMutation();

  useEffect(() => {
    if (!isModalOpen) setEditingRateCard(null);
  }, [isModalOpen]);

  const totalDeliveryPercentage = rateCards.reduce((sum, r) => sum + (Number(r.deliveryPercentage) || 0), 0);
  const otherDeliveryPercentageTotal = totalDeliveryPercentage - (editingRateCard?.deliveryPercentage ?? 0);
  const maxDeliveryPercentage = Math.max(0, 100 - otherDeliveryPercentageTotal);

  const handleSavePlan = async (name: string, description?: string): Promise<boolean> => {
    if (isTotalDeliveryPercentageOver100(rateCards)) {
      message.warning(t('totalDeliveryExceeds100'));
      return false;
    }
    await createPlan({
      name,
      description,
      settings: {
        totalBudget: settings.totalBudget,
        sellCPM: settings.sellCPM,
        displayCurrency: settings.displayCurrency,
        buyingPoint: settings.buyingPoint,
        tradingDealPercentage: settings.tradingDealPercentage,
        hardCostsTotal: settings.hardCostsTotal
      },
      rateCards: rateCards.map((card) => ({
        country: card.country,
        channel: card.channel,
        publisher: card.publisher,
        format: card.format,
        deliveryPercentage: card.deliveryPercentage,
        rowCurrency: card.rowCurrency,
        buyCPM: card.buyCPM,
        dspFeePercentage: card.dspFeePercentage
      }))
    }).unwrap();
    return true;
  };

  return (
    <>
      <PageSectionHeader>{t('eu6Calculator')}</PageSectionHeader>
      <Content>
        <PageContainer>
          {/* Calculator Settings */}
          <CalculatorSettings
            settings={settings}
            fxRates={fxRates}
            onUpdate={updateSettings}
            onUpdateFXRates={updateFXRates}
          />

          {/* KPI Summary Cards */}
          <KPISummary
            metrics={kpiMetrics}
            currency={settings.displayCurrency}
          />

          {/* Rates Table Section */}
          <div>
            <div style={{ position: 'relative', marginBottom: 16, minHeight: 40 }}>
              <SectionHeading style={{ margin: 0 }}>Media Plan Rates</SectionHeading>
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingRateCard(null);
                    openModal();
                  }}
                >
                  Add Rate to Plan
                </Button>
              </div>
            </div>

            {rateCards.length === 0 ? (
              <TableCard>
                <EmptyState
                  onAddRate={() => {
                    setEditingRateCard(null);
                    openModal();
                  }}
                />
              </TableCard>
            ) : (
              <RatesTable
                rateCards={rateCards}
                onDelete={deleteRateCard}
                onEdit={(rate) => {
                  setEditingRateCard(rate);
                  openModal();
                }}
                onDuplicate={duplicateRateCard}
                currency={settings.displayCurrency}
              />
            )}
          </div>

          {/* Add Rate Modal */}
          <AddRateModal
            open={isModalOpen}
            onClose={closeModal}
            onAdd={addRateCard}
            onEdit={(input) => {
              if (!editingRateCard) return;
              updateRateCard(editingRateCard.id, {
                country: input.country,
                channel: input.channel,
                publisher: input.publisher,
                format: input.format,
                deliveryPercentage: input.deliveryPercentage,
                rowCurrency: input.rowCurrency,
                buyCPM: input.buyCPM,
                dspFeeId: null,
                dspFeeName: input.dspFeeName,
                dspFeePercentage: input.dspFeePercentage
              });
              setEditingRateCard(null);
              closeModal();
            }}
            initialRateCard={editingRateCard}
            defaultCurrency={settings.displayCurrency}
            otherDeliveryPercentageTotal={otherDeliveryPercentageTotal}
            maxDeliveryPercentage={maxDeliveryPercentage}
            totalBudget={settings.totalBudget}
          />

          {/* Save Plan Modal */}
          <SavePlanModal
            open={isSaveModalOpen}
            onClose={() => setIsSaveModalOpen(false)}
            onSave={handleSavePlan}
            isLoading={isSaving}
          />
        </PageContainer>
      </Content>
    </>
  );
};

export default EU6Calculator;
